import Link from 'next/link';
import { ArrowDown, ArrowUpRight } from 'lucide-react';
import PlatformEngagement from './platform-engagement';
import CatalogDetailTheme from './catalog-detail-theme';
import '../app/v2/v2.css';
import './generic-catalog-detail.css';

export type Entry = Record<string, unknown>;

function plain(value: unknown): string {
  return typeof value === 'string' || typeof value === 'number' ? String(value) : '';
}

function imageFrom(entry: Entry): string {
  const assets = entry.assets && typeof entry.assets === 'object' ? entry.assets as Entry : {};
  return plain(entry.image) || plain(entry.iconUrl) || plain(assets.illustration) || plain(assets.icon);
}

function descriptionFrom(entry: Entry): string {
  return plain(entry.description) || plain(entry.summary) || plain(entry.cardIntro) || plain(entry.intro);
}

function displayValue(value: unknown): string {
  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') return String(value);
  if (Array.isArray(value)) return value.map(displayValue).filter(Boolean).join('、');
  return '';
}

export type CatalogDetailProps = {
  game: string;
  catalogName: string;
  summary: Entry;
  detail: Entry;
  itemRoute?: string;
};

export default function GenericCatalogDetail({ game, catalogName, summary, detail, itemRoute }: CatalogDetailProps) {
  const name = plain(detail.name) || plain(detail.title) || plain(summary.name) || '未命名条目';
  const image = imageFrom(detail) || imageFrom(summary);
  const description = descriptionFrom(detail) || descriptionFrom(summary);
  const excluded = new Set(['id', 'sourceId', 'name', 'title', 'image', 'iconUrl', 'description', 'summary', 'cardIntro', 'intro', 'assets', '_source']);
  const fields = Object.entries(detail).filter(([key, value]) => !excluded.has(key) && displayValue(value).trim());
  const nestedFields = Object.entries(detail).filter(([key, value]) => !excluded.has(key) && value !== null && typeof value === 'object' && !displayValue(value).trim());

  return <main className="v2-shell generic-detail-shell">
    <CatalogDetailTheme image={image} />
    <header className="v2-nav">
      <Link href={`/${game}`} className="v2-brand">
        <span>{game}<br /><small>GAME WIKI</small></span>
      </Link>
      <Link className="generic-detail-return" href={`/${game}/dcatalog`}>返回{catalogName}</Link>
    </header>
    <section className="v2-hero generic-detail-hero">
      <div className="v2-grid" aria-hidden="true" />
      <article className="v2-copy">
        <p className="v2-kicker">{catalogName} · ARCHIVE</p>
        <h1>{name}</h1>
        {plain(detail.subtitle) && <p className="v2-latin">{plain(detail.subtitle)}</p>}
        {description && <p className="v2-motto">{description}</p>}
        <div className="v2-identity generic-detail-identity">
          {fields.slice(0, 3).map(([key, value]) => <span key={key}><small>{key}</small><b>{displayValue(value)}</b></span>)}
        </div>
        <div className="v2-actions">
          <a href="#generic-detail-fields"><span><b>资料详情</b></span><ArrowDown /></a>
          <Link href={`/${game}/dcatalog`}><span><b>返回图鉴</b></span><ArrowUpRight /></Link>
        </div>
      </article>
      <article className="v2-art generic-detail-art">
        <div className="v2-aura" />
        {image ? <img src={image} alt={name} /> : <div className="generic-detail-art-empty">{name}</div>}
      </article>
    </section>
    <section className="v2-paper generic-detail-paper" id="generic-detail-fields">
      <div className="v2-content">
        <header className="v2-section-head"><div><p>REFERENCE DATA</p><h2>资料详情</h2></div><span>{catalogName}</span></header>
        {fields.length > 0 ? <dl className="generic-detail-fields">{fields.map(([key, value]) => <div key={key}><dt>{key}</dt><dd>{displayValue(value)}</dd></div>)}</dl> : <p className="generic-detail-empty">暂无更多资料</p>}
        {nestedFields.length > 0 && <section className="generic-detail-extended"><header className="v2-section-head"><div><p>ADDITIONAL DATA</p><h2>扩展资料</h2></div></header>{nestedFields.map(([key, value]) => <details key={key}><summary>{key}</summary><pre>{JSON.stringify(value, null, 2)}</pre></details>)}</section>}
      </div>
    </section>
    <PlatformEngagement
      linkUrl={itemRoute || `/${game}/dcatalog/${encodeURIComponent(String(summary.id || detail.id || name))}`}
      subject={catalogName}
      title={name}
      maxWidth={1080}
    />
  </main>;
}
